import type { ReactNode } from 'react';
import { modelState } from '@/utils/modelState';
import { QueryBuilder } from './QueryBuilder';
import { v4 as uuidv4 } from 'uuid';
import { OrderItemConditionComponent } from './OrderItemConditionComponent';

export namespace OrderItemCondition {

    export function buildRegistryEntry(
        roomTypes: { key: string; name: string }[],
        programTypes: { key: string; name: string }[],
        occupantTypes: { key: string; name: string }[]): QueryBuilder.ConditionRegistryEntry {
        return {
            key: "OrderItem",
            name: "Order Item",
            isCondition: isOrderItemCondition,
            create: createOrderItemCondition,
            hydrate: hydrateOrderItemCondition,
            serialize: serializeOrderItemCondition,
            deserialize: deserializeOrderItemCondition,
            renderComponent: (val) => renderOrderItemComponent(val as OrderItemCondition, roomTypes, programTypes, occupantTypes)
        };
    }

    export const OrderItemCondition = Symbol("OrderItemCondition");
    export interface OrderItemCondition extends QueryBuilder.ConditionNodeChild {
        [OrderItemCondition]: boolean;
        roomsSelected: modelState<boolean>;
        selectedRoomTypes: modelState<string[]>;
        programsSelected: modelState<boolean>;
        selectedProgramTypes: modelState<string[]>;
        halfDaySelected: modelState<boolean>;
        occupantsSelected: modelState<boolean>;
        selectedOccupantTypes: modelState<string[]>;
    }

    export function isOrderItemCondition(condition?: QueryBuilder.ConditionNodeChild): condition is OrderItemCondition {
        if (!condition) {
            return false;
        }

        return (condition as OrderItemCondition)[OrderItemCondition] === true;
    }

    export function createOrderItemCondition(skipHydration: boolean = false): OrderItemCondition {

        const condition: OrderItemCondition = {
            [QueryBuilder.Node]: true,
            [QueryBuilder.ConditionNodeChild]: true,
            [OrderItemCondition]: true,
            id: uuidv4(),
            version: modelState(0),
            roomsSelected: modelState(false),
            selectedRoomTypes: modelState<string[]>([]),
            programsSelected: modelState(false),
            selectedProgramTypes: modelState<string[]>([]),
            halfDaySelected: modelState(false),
            occupantsSelected: modelState(false),
            selectedOccupantTypes: modelState<string[]>([]),
            dispose: () => { }
        };

        if (!skipHydration) {
            hydrateOrderItemCondition(condition);
        }

        return condition;
    }

    function hydrateOrderItemCondition(condition: OrderItemCondition): void {
        const oldDispose = condition.dispose;

        const unsubscribeRoomsSelected = condition.roomsSelected.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeSelectedRoomTypes = condition.selectedRoomTypes.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeProgramsSelected = condition.programsSelected.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeSelectedProgramTypes = condition.selectedProgramTypes.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeHalfDaySelected = condition.halfDaySelected.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeOccupantsSelected = condition.occupantsSelected.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        const unsubscribeSelectedOccupantTypes = condition.selectedOccupantTypes.subscribe(() => {
            condition.version.setValue(condition.version.value + 1);
        });

        condition.dispose = () => {
            oldDispose();

            unsubscribeRoomsSelected();
            unsubscribeSelectedRoomTypes();
            unsubscribeProgramsSelected();
            unsubscribeSelectedProgramTypes();
            unsubscribeHalfDaySelected();
            unsubscribeOccupantsSelected();
            unsubscribeSelectedOccupantTypes();
        };
    }

    function serializeOrderItemCondition(condition: OrderItemCondition): QueryBuilder.ConditionChildNodeDtoWrapper {
        const orderItemDto: OrderItemConditionDto = {
            NodeId: condition.id,
            RoomsSelected: condition.roomsSelected.value,
            SelectedRoomTypes: condition.selectedRoomTypes.value,
            ProgramsSelected: condition.programsSelected.value,
            SelectedProgramTypes: condition.selectedProgramTypes.value,
            HalfDaySelected: condition.halfDaySelected.value,
            OccupantsSelected: condition.occupantsSelected.value,
            SelectedOccupantTypes: condition.selectedOccupantTypes.value,
        };

        return {
            Type: "OrderItem",
            Value: orderItemDto
        };
    }

    interface OrderItemConditionDto extends QueryBuilder.NodeDto {
        RoomsSelected: boolean;
        SelectedRoomTypes: string[];
        ProgramsSelected: boolean;
        SelectedProgramTypes: string[];
        HalfDaySelected: boolean;
        OccupantsSelected: boolean;
        SelectedOccupantTypes: string[];
    }

    function deserializeOrderItemCondition(orderItem: OrderItemConditionDto): OrderItemCondition {
        const condition = createOrderItemCondition(true);

        condition.roomsSelected.setValue(orderItem.RoomsSelected);
        condition.roomsSelected.commit();

        condition.selectedRoomTypes.setValue(orderItem.SelectedRoomTypes);
        condition.selectedRoomTypes.commit();

        condition.programsSelected.setValue(orderItem.ProgramsSelected);
        condition.programsSelected.commit();

        condition.selectedProgramTypes.setValue(orderItem.SelectedProgramTypes);
        condition.selectedProgramTypes.commit();

        condition.halfDaySelected.setValue(orderItem.HalfDaySelected);
        condition.halfDaySelected.commit();

        condition.occupantsSelected.setValue(orderItem.OccupantsSelected || false);
        condition.occupantsSelected.commit();

        condition.selectedOccupantTypes.setValue(orderItem.SelectedOccupantTypes || []);
        condition.selectedOccupantTypes.commit();

        return condition;
    }

    function renderOrderItemComponent(
        value: OrderItemCondition,
        roomTypes: { key: string; name: string }[],
        programTypes: { key: string; name: string }[],
        occupantTypes: { key: string; name: string }[]): ReactNode {
        return (
            <OrderItemConditionComponent
                condition={value}
                roomTypes={roomTypes}
                programTypes={programTypes}
                occupantTypes={occupantTypes} />
        );
    }
}
